/**
 * AI Detector score gauge — the semicircle dial from the design sample.
 *
 * Shared by the live check (ai-detector.js) and the stored result page
 * (ai-detector/history/show), so both draw the same thing. Styles live in
 * ds/ai-detector.css (.gauge, .g-arc, .g-needle, .g-center).
 */
window.DetectorGauge = (function () {
    'use strict';

    var GAUGE_GLOW = { ai: '192,69,63', likely: '194,91,0', mixed: '232,147,12', human: '14,123,84' };
    var VERDICT_EMOJI = { ai: '🤖', likely: '🤖', mixed: '⚖️', human: '✍️' };

    // Four bands, the same ones the API's sentence colors use:
    // 0-39 green (human), 40-59 yellow (mixed), 60-79 orange (likely), 80-100 red (ai).
    function scoreTheme(score) {
        return score >= 80 ? 'ai' : score >= 60 ? 'likely' : score >= 40 ? 'mixed' : 'human';
    }

    // Semicircle dial: gradient arc from AI (left) to Human (right), tick marks
    // every 5%, and a needle that swings to the score.
    // Sample v2 turned the dial around: HUMAN sits on the left, AI on the right,
    // so the needle swings to the right as the AI score grows.
    // labels: { ai, human, caption } in the page's language; English if left out.
    function gaugeHTML(score, theme, labels) {
        labels = labels || {};
        var cx = 115, cy = 116, R = 92;
        var needleDeg = score * 1.8 - 90;
        var dotColor = score >= 80 ? 'var(--danger)' : score >= 60 ? 'var(--warn)' : score >= 40 ? 'var(--mark)' : 'var(--green)';
        var glow = GAUGE_GLOW[theme];
        var ticks = '';

        for (var p = 0; p <= 100; p += 5) {
            var major = p % 25 === 0;
            var a = (p * 1.8 - 180) * Math.PI / 180;
            var r1 = R - 16, r2 = R - (major ? 27 : 22);
            ticks += '<line x1="' + (cx + r1 * Math.cos(a)).toFixed(1) + '" y1="' + (cy + r1 * Math.sin(a)).toFixed(1) +
                '" x2="' + (cx + r2 * Math.cos(a)).toFixed(1) + '" y2="' + (cy + r2 * Math.sin(a)).toFixed(1) +
                '" stroke="var(--muted)" stroke-opacity="' + (major ? '.75' : '.35') +
                '" stroke-width="' + (major ? '2.2' : '1.3') + '" stroke-linecap="round"/>';
        }

        return '<div class="g-halo" style="background:radial-gradient(circle, rgba(' + glow + ',.9) 0%, rgba(' + glow + ',0) 70%)"></div>' +
            '<svg viewBox="0 0 230 138" aria-hidden="true">' +
                '<defs><linearGradient id="gGrad" x1="0" y1="0" x2="1" y2="0">' +
                    '<stop offset="0" stop-color="var(--green)"/><stop offset=".45" stop-color="var(--mark)"/>' +
                    '<stop offset=".7" stop-color="#E2A23B"/><stop offset=".9" stop-color="hsl(8,62%,48%)"/>' +
                '</linearGradient>' +
                '<linearGradient id="gNdl" x1="0" y1="0" x2="1" y2="0"><stop offset="0" stop-color="var(--needle-a)"/><stop offset=".5" stop-color="var(--needle-b)"/><stop offset="1" stop-color="var(--needle-c)"/></linearGradient></defs>' +
                '<path d="M ' + (cx - R) + ' ' + cy + ' A ' + R + ' ' + R + ' 0 0 1 ' + (cx + R) + ' ' + cy + '" fill="none" stroke="var(--surface)" stroke-width="27" stroke-linecap="round" style="filter:drop-shadow(0 3px 6px rgba(20,40,30,.14))"/>' +
                '<path class="g-arc" d="M ' + (cx - R) + ' ' + cy + ' A ' + R + ' ' + R + ' 0 0 1 ' + (cx + R) + ' ' + cy + '" stroke="url(#gGrad)" style="filter:drop-shadow(0 0 8px rgba(' + glow + ',.35))"/>' +
                ticks +
                '<g class="g-needle" style="transform-origin:' + cx + 'px ' + cy + 'px;transform:rotate(-90deg)" data-deg="' + needleDeg.toFixed(1) + '">' +
                    '<path d="M ' + cx + ' ' + (cy + 6) + ' L ' + (cx - 4.5) + ' ' + cy + ' L ' + cx + ' ' + (cy - R + 30) + ' L ' + (cx + 4.5) + ' ' + cy + ' Z" fill="url(#gNdl)"/>' +
                    '<path d="M ' + cx + ' ' + (cy - R + 30) + ' L ' + (cx - 2) + ' ' + (cy - 9) + ' L ' + cx + ' ' + (cy - 11) + ' Z" fill="#FFF" opacity=".22"/>' +
                '</g>' +
                '<circle cx="' + cx + '" cy="' + cy + '" r="11.5" fill="var(--gauge-hub)"/>' +
                '<circle cx="' + cx + '" cy="' + cy + '" r="11" fill="none" stroke="#FFF" stroke-opacity=".25"/>' +
                '<circle cx="' + cx + '" cy="' + cy + '" r="4.5" fill="' + dotColor + '"/>' +
                '<text x="' + (cx + R) + '" y="' + (cy + 18) + '" text-anchor="middle" font-family="var(--font-mono)" font-size="10" letter-spacing="1" fill="hsl(8,62%,44%)">' + escapeText(labels.ai || 'AI') + '</text>' +
                '<text x="' + (cx - R) + '" y="' + (cy + 18) + '" text-anchor="middle" font-family="var(--font-mono)" font-size="10" letter-spacing="1" fill="var(--green-deep)">' + escapeText(labels.human || 'HUMAN') + '</text>' +
            '</svg>' +
            '<div class="g-center"><span class="g-num"><span class="g-score">0</span><small>%</small></span><span class="g-cap">' + escapeText(labels.caption || 'AI probability') + '</span></div>';
    }

    function escapeText(value) {
        return String(value).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    }

    // Arc draws in, needle springs to the score, the number counts up.
    function animateGauge(gauge, verdict, score) {
        var arc = gauge.querySelector('.g-arc');
        var needle = gauge.querySelector('.g-needle');
        var number = gauge.querySelector('.g-score');
        var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        if (reduced) {
            arc.style.strokeDashoffset = '0';
            needle.style.transform = 'rotate(' + needle.dataset.deg + 'deg)';
            number.textContent = score;
            gauge.classList.add('done');
            verdict.classList.add('show');
            return;
        }

        requestAnimationFrame(function () {
            requestAnimationFrame(function () {
                arc.style.strokeDashoffset = '0';
                needle.style.transform = 'rotate(' + needle.dataset.deg + 'deg)';
                verdict.classList.add('show');
            });
        });

        var DELAY = 480, DURATION = 1700, start = null;
        window.setTimeout(function () {
            window.requestAnimationFrame(function step(now) {
                if (start === null) start = now;
                var t = Math.min(1, (now - start) / DURATION);
                number.textContent = Math.round(score * (1 - Math.pow(1 - t, 3)));
                if (t < 1) window.requestAnimationFrame(step);
                else gauge.classList.add('done');
            });
        }, DELAY);
    }

    // Compact dial for the verdict card (sample v2): same scale, no number inside —
    // the big percentage lives next to it in .rc-num.
    // ariaLabel: the page's "AI probability: N%"; English if left out.
    function miniGaugeHTML(score, theme, ariaLabel) {
        var cx = 62, cy = 64, R = 48;
        var needleDeg = score * 1.8 - 90;
        var dotColor = score >= 80 ? 'var(--danger)' : score >= 60 ? 'var(--warn)' : score >= 40 ? 'var(--mark)' : 'var(--green)';
        var glow = GAUGE_GLOW[theme];

        return '<div class="gauge--mini" role="img" aria-label="' + escapeText(ariaLabel || 'AI probability: ' + score + '%') + '">' +
            '<svg viewBox="0 2 124 76" aria-hidden="true">' +
                '<defs><linearGradient id="gGradM" x1="0" y1="0" x2="1" y2="0">' +
                    '<stop offset="0" stop-color="var(--green)"/><stop offset=".45" stop-color="var(--mark)"/>' +
                    '<stop offset=".7" stop-color="#E2A23B"/><stop offset=".9" stop-color="hsl(8,62%,48%)"/>' +
                '</linearGradient></defs>' +
                '<path d="M ' + (cx - R) + ' ' + cy + ' A ' + R + ' ' + R + ' 0 0 1 ' + (cx + R) + ' ' + cy + '" fill="none" stroke="var(--surface)" stroke-width="15" stroke-linecap="round" style="filter:drop-shadow(0 2px 4px rgba(20,40,30,.14))"/>' +
                '<path class="g-arc-m" d="M ' + (cx - R) + ' ' + cy + ' A ' + R + ' ' + R + ' 0 0 1 ' + (cx + R) + ' ' + cy + '" stroke="url(#gGradM)" style="filter:drop-shadow(0 0 6px rgba(' + glow + ',.35))"/>' +
                '<g class="g-needle" style="transform-origin:' + cx + 'px ' + cy + 'px;transform:rotate(-90deg)" data-deg="' + needleDeg.toFixed(1) + '">' +
                    '<path d="M ' + cx + ' ' + (cy + 4) + ' L ' + (cx - 3) + ' ' + cy + ' L ' + cx + ' ' + (cy - R + 14) + ' L ' + (cx + 3) + ' ' + cy + ' Z" fill="var(--needle-b)"/>' +
                '</g>' +
                '<circle cx="' + cx + '" cy="' + cy + '" r="7" fill="var(--gauge-hub)"/>' +
                '<circle cx="' + cx + '" cy="' + cy + '" r="3" fill="' + dotColor + '"/>' +
                '<text x="' + (cx + R) + '" y="' + (cy + 12) + '" text-anchor="middle" font-family="var(--font-mono)" font-size="8" letter-spacing=".8" fill="hsl(8,62%,44%)">AI</text>' +
                '<text x="' + (cx - R) + '" y="' + (cy + 12) + '" text-anchor="middle" font-family="var(--font-mono)" font-size="8" letter-spacing=".8" fill="var(--green-deep)">HUMAN</text>' +
            '</svg>' +
        '</div>';
    }

    // The card fades in, its dial draws and springs, and the percentage counts up.
    function animateCard(card, score) {
        var gauge = card.querySelector('.gauge--mini');
        var arc = card.querySelector('.g-arc-m');
        var needle = card.querySelector('.g-needle');
        var number = card.querySelector('.rc-num > span');
        var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        function settle() {
            if (arc) arc.style.strokeDashoffset = '0';
            if (needle) needle.style.transform = 'rotate(' + needle.dataset.deg + 'deg)';
            card.classList.add('show');
        }

        if (reduced) {
            settle();
            if (number) number.textContent = score;
            if (gauge) gauge.classList.add('done');
            return;
        }

        requestAnimationFrame(function () { requestAnimationFrame(settle); });

        var DELAY = 480, DURATION = 1700, start = null;
        window.setTimeout(function () {
            window.requestAnimationFrame(function step(now) {
                if (start === null) start = now;
                var t = Math.min(1, (now - start) / (DURATION - DELAY));
                if (number) number.textContent = Math.round(score * (1 - Math.pow(1 - t, 3)));
                if (t < 1) window.requestAnimationFrame(step);
                else if (gauge) gauge.classList.add('done');
            });
        }, DELAY);
    }

    return {
        theme: scoreTheme,
        emoji: function (theme) { return VERDICT_EMOJI[theme]; },
        html: gaugeHTML,
        miniHtml: miniGaugeHTML,
        animate: animateGauge,
        animateCard: animateCard
    };
})();
